import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  Typography,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  createLibrary,
  updateLibrary,
  selectLibraryLoading,
  selectLibraryError,
  clearError,
} from "../../../store/slices/libraryAdminSlice";

const LibraryForm = ({ open, onClose, library, chitalishteId, feedbackId, onSubmit }) => {
  const dispatch = useDispatch();
  const loading = useSelector(selectLibraryLoading);
  const error = useSelector(selectLibraryError);

  const [formData, setFormData] = useState({
    name: "",
    registrationNumber: "",
    address: "",
    bulstat: "",
    chairman: "",
    email: "",
    municipality: "",
    phone: "",
    region: "",
    town: "",
    chitalishteId: chitalishteId,
    feedbackId: "",
  });

  const [errors, setErrors] = useState({});
  const [autoUpdateFeedback, setAutoUpdateFeedback] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (open) {
      if (library) {
        setFormData({
          name: library.name || "",
          registrationNumber: library.registrationNumber || "",
          address: library.address || "",
          bulstat: library.bulstat || "",
          chairman: library.chairman || "",
          email: library.email || "",
          municipality: library.municipality || "",
          phone: library.phone || "",
          region: library.region || "",
          town: library.town || "",
          chitalishteId: library.chitalishteId || chitalishteId,
          feedbackId: feedbackId || "",
        });
      } else {
        setFormData({
          name: "",
          registrationNumber: "",
          address: "",
          bulstat: "",
          chairman: "",
          email: "",
          municipality: "",
          phone: "",
          region: "",
          town: "",
          chitalishteId: chitalishteId,
          feedbackId: feedbackId || "",
        });
      }
      setAutoUpdateFeedback(!!feedbackId);
      setErrors({});
    }
  }, [library, chitalishteId, feedbackId, open]);

  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setConfirmOpen(true);
  };

  const handleConfirm = () => {
    const libraryData = {
      ...formData,
      registrationNumber: formData.registrationNumber
        ? Number(formData.registrationNumber)
        : null,
      shouldUpdateFeedback:
        autoUpdateFeedback && formData.feedbackId && formData.feedbackId.trim(),
    };
    onSubmit(libraryData);
    setConfirmOpen(false);
  };

  const canShowAutoUpdateCheckbox = () => {
    return (
      formData.feedbackId &&
      formData.feedbackId.trim() !== "" &&
      !errors.feedbackId
    );
  };

  const getConfirmationMessage = () => {
    if (canShowAutoUpdateCheckbox()) {
      if (autoUpdateFeedback) {
        return "Сигурни ли сте, че искате да запазите промените? Статусът на свързаната обратна връзка ще бъде променен на 'Обработен'.";
      } else {
        return "Сигурни ли сте, че искате да запазите промените? Статусът на свързаната обратна връзка НЯМА да бъде променен.";
      }
    }
    return "Сигурни ли сте, че искате да запазите промените по библиотеката?";
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {library ? "Редактиране на Библиотека" : "Създаване на Библиотека"}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error">
              {typeof error === "string" ? error : JSON.stringify(error)}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="name"
                label="Име"
                value={formData.name}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="registrationNumber"
                label="Регистрационен номер"
                value={formData.registrationNumber}
                onChange={handleChange}
                fullWidth
                required
                type="number"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="address"
                label="Адрес"
                value={formData.address}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="bulstat"
                label="БУЛСТАТ"
                value={formData.bulstat}
                onChange={handleChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="town"
                label="Населено място"
                value={formData.town}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="municipality"
                label="Община"
                value={formData.municipality}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="region"
                label="Област"
                value={formData.region}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="chairman"
                label="Председател"
                value={formData.chairman}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="phone"
                label="Телефон"
                value={formData.phone}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="email"
                label="Имейл"
                value={formData.email}
                onChange={handleChange}
                fullWidth
                type="email"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Feedback ID (опционално)"
                name="feedbackId"
                value={formData.feedbackId}
                onChange={handleChange}
                error={!!errors.feedbackId}
                helperText={
                  errors.feedbackId ||
                  "Свържи тази промяна с конкретен feedback (UUID формат)"
                }
                placeholder="напр. 123e4567-e89b-12d3-a456-426614174000"
              />
            </Grid>
            {canShowAutoUpdateCheckbox() && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={autoUpdateFeedback}
                      onChange={(e) => setAutoUpdateFeedback(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Автоматично обнови статуса на обратната връзка на 'Обработен'"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Отказ</Button>
          <Button onClick={handleSubmit} color="primary" disabled={loading}>
            {loading ? (
              <CircularProgress size={24} />
            ) : library ? (
              "Запази промени"
            ) : (
              "Създай"
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Потвърждение</DialogTitle>
        <DialogContent>
          <Typography>{getConfirmationMessage()}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Отказ</Button>
          <Button onClick={handleConfirm} variant="contained" color="primary">
            Потвърди
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LibraryForm;
